import type { IChitCommentData, IChitData, IChitOptionData } from '@/models/chit'
import type { IChitService, IChitFilterParam, IChitSubmitParams } from './chit.types'
import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import axios from '@/axios'
import type { IPaginatedListData } from '../types'

/**
 * A class representing an ChitService API handler.
 */
export class ChitService implements IChitService {
  private endpoint: string

  // ChitService constructor
  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/chits/'
  }

  // Method to fetch a list of chits from the API
  fetchChits(filter?: IChitFilterParam): Promise<IPaginatedListData<IChitData>> {
    const config: AxiosRequestConfig | undefined = filter ? { params: filter } : undefined
    return this.axios.get(`${this.endpoint}`, config).then(({ data }) => data)
  }

  // Method to fetch a single chit by their ID from the API
  fetchChit(chit_number: number): Promise<IChitData> {
    return this.axios.get(`${this.endpoint}${chit_number}/`).then(({ data }) => data)
  }

  // Method to create a new chit on the API
  createChit(data: IChitData): Promise<IChitData> {
    return this.axios.post(`${this.endpoint}`, data).then(({ data }) => data)
  }

  // Method to update an existing chit on the API
  updateChit(data: IChitData): Promise<IChitData> {
    return this.axios.patch(`${this.endpoint}${data.chit_number}/`, data).then(({ data }) => data)
  }

  // Method to delete a chit by their ID from the API
  deleteChit(chit_number: number): Promise<void> {
    return this.axios.delete(`${this.endpoint}${chit_number}/`).then(({ data }) => data)
  }

  //chit options
  fetchChitOptions(type?: string): Promise<IChitOptionData[]> {
    return this.axios
      .get(`${this.endpoint}options/`, {
        params: { type }
      })
      .then(({ data }) => data)
  }

  //exports
  exportChitExcel(filter?: IChitFilterParam): Promise<Blob> {
    const config: AxiosRequestConfig | undefined = filter ? { params: filter } : undefined
    return this.axios
      .get(`${this.endpoint}export-xlsx/`, {
        ...config,
        responseType: 'blob',
        timeout: 30 * 1000 // 30 seconds
      })
      .then(({ data }) => data)
  }

  exportChitPdf(chitNumber: number): Promise<Blob> {
    return this.axios
      .get(`${this.endpoint}${chitNumber}/export-pdf/`, {
        responseType: 'blob',
        timeout: 30 * 1000 // 30 seconds
      })
      .then(({ data }) => data)
  }

  // Method to submit a chit for approval
  submitChit(payload: IChitSubmitParams): Promise<IChitData> {
    return this.axios
      .post(`${this.endpoint}${payload.chit_number}/submit/`, payload)
      .then(({ data }) => data)
  }

  // Method to approve a chit
  approveChit(chit_number: number): Promise<IChitData> {
    return this.axios.post(`${this.endpoint}${chit_number}/approve/`).then(({ data }) => data)
  }

  // Method to cancel a chit
  cancelChit(chit_number: number): Promise<IChitData> {
    return this.axios.post(`${this.endpoint}${chit_number}/cancel/`).then(({ data }) => data)
  }

  // Method to fetch comments for a chit
  fetchChitComments(chit_number: number): Promise<IChitCommentData[]> {
    return this.axios.get(`${this.endpoint}${chit_number}/comments/`).then(({ data }) => data)
  }
  // Method to fetch a specific chit comment
  fetchChitComment(chit_number: number, id: number): Promise<IChitCommentData> {
    return this.axios.get(`${this.endpoint}${chit_number}/comments/${id}/`).then(({ data }) => data)
  }
  // Method to create a new comment for a chit
  createChitComment(chit_number: number, data: IChitCommentData): Promise<IChitCommentData> {
    return this.axios
      .post(`${this.endpoint}${chit_number}/comments/`, data)
      .then(({ data }) => data)
  }
  // Method to update an existing chit comment
  updateChitComment(chit_number: number, data: IChitCommentData): Promise<IChitCommentData> {
    return this.axios
      .patch(`${this.endpoint}${chit_number}/comments/${data.id}/`, data)
      .then(({ data }) => data)
  }
  // Method to delete a chit comment
  deleteChitComment(chit_number: number, id: number): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${chit_number}/comments/${id}/`)
      .then(({ data }) => data)
  }
}

// Default export instantiation of the ChitService
const chitService = new ChitService(axios)
export default chitService
